'use client';
import { twMerge } from 'tailwind-merge';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { convertWeight } from '../../../../helpers/conversion';
import { Fragment, useState } from 'react';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { useUserInfo } from '../../../../helpers/hooks/useUserInfo';
import { GetSpeciesAreaRecordsList_species_edges_node } from '../../../../interfaces/graphql';
import phone from '../../assets/PhonePreviewDesktop.png';
import phoneMobile from '../../assets/PhonePreviewMobile.png';
interface IProps {
  className?: string;
  topSpecies: GetSpeciesAreaRecordsList_species_edges_node[];
  id?: string;
  title?: string;
  appDownloadSourceId?: string;
  appDownloadHeader?: string;
  appDownloadText?: string;
}

export const StateRecordsSection = ({
  className,
  topSpecies,
  id = 'state-records-section',
  title = 'State Records of caught fish',
  appDownloadSourceId,
  appDownloadHeader = `Discover records for every species`,
  appDownloadText = `See average catches and state records right in the Fishbrain app. Scan the QR code to download and explore more!`,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const { user } = useUserInfo();

  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'StateRecordsSection',
        source_id: appDownloadSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      className={twMerge(
        'scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)',
        className,
      )}
      id={id}
    >
      <div className="bg-sea-foam sm:bg-lake-fog flex size-full flex-col justify-center gap-2 sm:rounded-xl sm:px-8 sm:pt-8 sm:pb-2 md:px-8 md:pt-8">
        <Heading variant="sm" className="mb-2 text-left">
          {title}
        </Heading>
        <div className="grid grid-cols-[1fr_max-content]">
          <Text as="span" className="border-twilight m-0 border-b p-4" variant="sm">
            Species
          </Text>
          <Text as="span" className="border-twilight m-0 border-b p-4" variant="sm">
            Weight
          </Text>
          {topSpecies
            .filter(species => species.areaSpecies?.recordWeightInKg != null)
            .sort((a, b) => {
              const weightA = a.areaSpecies?.recordWeightInKg ?? 0;
              const weightB = b.areaSpecies?.recordWeightInKg ?? 0;
              return weightB - weightA; // Sort descending
            })
            .map(({ areaSpecies, displayName }, index) => (
              <Fragment key={index}>
                <Text
                  as="span"
                  className="border-twilight m-0 border-b p-4 font-semibold"
                  variant="sm"
                >
                  {displayName}
                </Text>

                <Text as="span" className="border-twilight m-0 border-b border-l p-4" variant="sm">
                  {convertWeight(areaSpecies?.recordWeightInKg ?? 0, user)}
                </Text>
              </Fragment>
            ))}
        </div>
        <Button size="sm" className="p-2" variant="textPrimary" onClick={handleCTAClick}>
          See more species
        </Button>
      </div>
      <AppDownloadModal
        appDownloadSource="StateRecordsSection"
        appDownloadSourceId={appDownloadSourceId}
        modalHeader={appDownloadHeader}
        modalText={appDownloadText}
        image={phone}
        imageMobile={phoneMobile}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
